/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../../contexts/localeContext.js";
import { usePaymentService } from "../../../services/paymentService.js";
import { usePolicyService } from "../../../services/policyService.js";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from "../../../contexts/userContext";
import { cardText } from "../../../helpers/general.js";

/* Components */
import UCButton from "../../buttons";
import UCToggle from "../../uctoggle";
import { ReactComponent as CardIcon } from "../../../resources/images/credit-card-03.svg";
import { ReactComponent as RemovePolicyIcon } from "../../../resources/images/aa-remove-policy.svg";
import { ReactComponent as PolicyIcon } from "../../../resources/images/aa-create-policy.svg";
import { ReactComponent as MatercardIcon } from "../../../resources/images/aa-mastercard.svg"
import { ReactComponent as VisaIcon } from '../../../resources/images/aa-visa.svg';
import { ReactComponent as AmexIcon } from '../../../resources/images/aa-amex.svg';
import { ReactComponent as DiscoverIcon } from '../../../resources/images/aa-discover.svg';
import { ReactComponent as DinnersClubIcon } from '../../../resources/images/aa-dinnersclub.svg';
import { ReactComponent as UATPIcon } from '../../../resources/images/aa-uatp.svg';
import { ReactComponent as JcbIcon } from '../../../resources/images/aa-jcb.svg';
import { ReactComponent as UnionPayIcon } from '../../../resources/images/aa-unionpay.svg';
import { ReactComponent as GenericPayIcon } from '../../../resources/images/aa-genericpay.svg';
import { ReactComponent as ViasIcon } from '../../../resources/images/cc-alaska.svg';
import ChangePaymentModal from "../../modals/changePaymentModal";
import ChangePolicyModal from "../../modals/changePolicyModal";
import SettingsTabSkeleton from "../skeletonSettingsTab";

/* Styles */
import styles from "./styles.js"

const getCardIcon = vendorCode => {
  switch(vendorCode) {
    case "CA":
      return <MatercardIcon/>
    case "VI":
      return <VisaIcon/>
    case "AX":
      return <AmexIcon/>
    case "DS":
      return <DiscoverIcon/>
    case "DC":
      return <DinnersClubIcon/>
    case "TP":
      return <UATPIcon/>
    case "JC":
      return <JcbIcon/>
    case "CU":
      return <UnionPayIcon/>
    case "VIAS":
    case "AS":
      return <ViasIcon/>
    default:
      return <GenericPayIcon/>
  }
}

const getCardText = ( vendorCode, cardNumberMask) => {
  return <FormattedMessage id="teamDetails.settingsTab.paymentMethod" values={{vendorName: cardText(vendorCode), cardNumberMask: cardNumberMask}}/>
}

const SettingsTab = ({teamId}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const { checkPermission } = useHasPermission();
  const globalLocale = useLocaleContext();
  const userDetails = useUserContext();

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const { updateTeamSettingsToggle, paymentMethods, getPaymentMethods, maxCardsAssignment } = usePaymentService();
  const { getTeamPolicy, teamPolicy } = usePolicyService();
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showChangePaymentModal, setShowChangePaymentModal] = useState(false);
  const [showChangePolicyModal, setShowChangePolicyModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    (async () => {
      setShowSkeleton(true)
      if (checkPermission("F_TEAMDASHBOARD_VIEWASSIGNEDPAYMENTS")) {
        await getPaymentMethods(teamId)
      }
      await getTeamPolicy(teamId)
      setShowSkeleton(false)
    })();
  }, []);

  useEffect(() => {
    if(paymentMethods) {
      let assignedPayments = []
      paymentMethods.length > 0 && paymentMethods.map((payment, i) => {
        if (payment?.fSelected) {
          assignedPayments.push(payment)
        }
      });
      setPayments(assignedPayments)
    }

    if(teamPolicy) {
      setPolicy(teamPolicy)
    } else setPolicy()
  }, [paymentMethods, teamPolicy]);

  const onChangeRequirePayment = async (e) => {
    const toggleValue = e.target.checked;

    const reqBody = {
      fopUUID: payments?.fopUUID, // todo
      groupEntityID: teamId,
      toggle: toggleValue
    };
    await updateTeamSettingsToggle(reqBody);
  };

  const getExpiryDateText = expirationDate => {
    let momentDate =  moment(expirationDate, 'MM/YYYY')
    if (momentDate.isValid()) {
      let date =  momentDate.toDate()
      let formattedExpiryDate = moment(date).locale(globalLocale).format("MMM YYYY")
      let now = new Date();
      let dateEntered = new Date(date).getTime();
      let sixMonthAfterNow = new Date(now).setMonth(now.getMonth() + 6);

      if(dateEntered < now.getTime()) {
        return <div className={classes.redExpiredDate}><FormattedMessage id="paymentsGrid.expiredDate" values={{expiryDate: formattedExpiryDate}}/></div>
      } else if(dateEntered < sixMonthAfterNow) {
        return <div className={classes.redExpiredDate}><FormattedMessage id="paymentsGrid.willExpireDate" values={{expiryDate: formattedExpiryDate}}/></div>
      } else {
        return <div className={classes.expiredDate}><FormattedMessage id="paymentsGrid.willExpireDate" values={{expiryDate: formattedExpiryDate}}/></div>
      }
    }
    return ""
  }

  const changePaymentCallback = async (paymentMethods) => {
    setPayments(paymentMethods)
    await getPaymentMethods(teamId)
  }

  const changePolicyCallback = async () => {
    await getTeamPolicy(teamId)
  }

  return (
    <div className={classes.settingsTab}>
      <div className={classes.details}>
        <div className={classes.title}>
          <FormattedMessage id="teamDetails.settingsTab.title"/>
        </div>
        <div className={classes.description}>
          <FormattedMessage id={`teamDetails.settingsTab.description.${providerCode}`}/>
        </div>
      </div>
      {
        checkPermission("F_TEAMDASHBOARD_VIEWASSIGNEDPAYMENTS") &&
        <>
          <div className={classes.dividerContainer}>
            <hr className={classes.border} />
          </div>
          <div className={`row ${classes.container}`}>
            <div className={`col-12 col-xl-4 ${classes.leftContainer}`}>
              <div className={classes.personalDetails}>
                <FormattedMessage id="teamDetails.settingsTab.paymentTitle"/>
              </div>
              <div className={classes.description}>
                <FormattedMessage id={`teamDetails.settingsTab.paymentDescription.${providerCode}`}/>
              </div>
            </div>
            {showSkeleton ? (<SettingsTabSkeleton />) : (
              <div className={`col-12 col-xl-8 ${classes.rightContainer}`}>
                <div className={classes.rightTopContainer}>
                  <div>
                  {
                    payments?.length ?
                      payments.map(payment => (
                        <div className={classes.rightTopContainerWrapper}>
                          <div className={classes.cardIcon}>{getCardIcon(payment?.vendorCode)}</div>
                          <div className={classes.cardDetails}>
                            <h1 className={classes.cardNumberText}>{getCardText(payment?.vendorCode, payment?.cardNumberMask)}</h1>
                            <p>{getExpiryDateText(payment?.expirationDate)}</p>
                          </div>
                        </div>
                      ))
                    :
                    <div className={classes.rightTopContainerWrapper}>
                      <div className={classes.iconWrapper}><CardIcon/></div>
                      <div className={classes.containerDescription}>
                        <h1><FormattedMessage id="teamDetails.settingsTab.paymentContainerTitle"/></h1>
                        <p><FormattedMessage id="teamDetails.settingsTab.paymentContainerDescription"/></p>
                      </div>
                    </div>
                  }
                  </div>
                  {
                    checkPermission("F_EDIT_GROUP_ASSIGNPAYMENT") && !userDetails?.manageCompany &&
                    <UCButton
                      type="default"
                      className={classes.changeBtn}
                      text={<FormattedMessage id={`teamDetails.settingsTab.changeButton.${providerCode}`}/>}
                      onClick={() => setShowChangePaymentModal(true)}
                    />
                  }
                </div>
                {
                  checkPermission("F_TEAMDASHBOARD_VIEWREQUIREDPAYMENT") && payments.length &&
                  <div className={classes.toggleContainer}>
                    <UCToggle
                      onChange={(e) => onChangeRequirePayment(e)}
                      icons={null}
                      defaultChecked={payments?.fRequiredPurchase} //todo
                    />
                    <div>
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="teamDetails.toggleTitle"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="teamDetails.toggleDescription"/>
                      </div>
                    </div>
                  </div>
                }
              </div>
            )}
          </div>
        </>
      }
      {
        checkPermission("F_TEAMDASHBOARD_VIEWASSIGNEDPOLICY") &&
        <>
          <div className={classes.dividerContainer}>
            <hr className={classes.border}/>
          </div>
          <div className={`row ${classes.container}`}>
            <div className={`col-12 col-xl-4 ${classes.leftContainer}`}>
              <div className={classes.personalDetails}>
                <FormattedMessage id="teamDetails.settingsTab.policyTitle"/>
              </div>
              <div className={classes.description}>
                <FormattedMessage id="teamDetails.settingsTab.policyDescription"/>
              </div>
            </div>
            {showSkeleton ? (<SettingsTabSkeleton />) : (
              <div className={`col-12 col-xl-8 ${classes.rightContainer}`}>
                <div className={classes.rightTopContainer}>
                  <div>
                    <div className={classes.rightTopContainerWrapper}>
                      <div className={classes.iconWrapper}>{policy ? <PolicyIcon /> : <RemovePolicyIcon />}</div>
                      <div className={classes.containerDescription}>
                        <h1>{policy ? policy?.policyName :
                          <FormattedMessage id="teamDetails.settingsTab.policyContainerTitle" />}</h1>
                        {policy ? <p><FormattedMessage
                          id="changePolicyModal.lastUpdated" /> {moment(policy?.updatedOn).locale(globalLocale).format('D MMM YYYY')}
                        </p> : <p><FormattedMessage id="teamDetails.settingsTab.policyContainerDescription" /></p>}
                      </div>
                    </div>
                  </div>
                  {
                    checkPermission('F_EDIT_GROUP_ASSIGNPOLICY') &&
                    <UCButton
                      type="default"
                      className={classes.changeBtn}
                      text={<FormattedMessage id={`teamDetails.settingsTab.changeButton.${providerCode}`} />}
                      onClick={() => setShowChangePolicyModal(true)}
                    />
                  }
                </div>
              </div>
            )}
          </div>
        </>
      }
      <ChangePaymentModal
        showModal={showChangePaymentModal}
        setShowModal={setShowChangePaymentModal}
        callback={changePaymentCallback}
        getCardIcon={getCardIcon}
        getCardText={getCardText}
        getExpiryDateText={getExpiryDateText}
        paymentMethods={paymentMethods}
        teamId={teamId}
        payments={payments}
        maxCardsAssignment={maxCardsAssignment}
      />
      <ChangePolicyModal
        showModal={showChangePolicyModal}
        setShowModal={setShowChangePolicyModal}
        setPolicy={setPolicy}
        teamId={teamId}
        policy={teamPolicy}
        callback={changePolicyCallback}
      />
    </div>
  )
}

export default SettingsTab;
